import { LISTING_STATUSES } from "./listing-eligibility";

export type ListingFilterKind = "list" | "toggle" | "range";

interface ListingFacet {
	kind: ListingFilterKind;
	param: string;
	matchesAll?: true;
}

// Must match the facet attributes in search-sync products-index-settings.ts.
const LISTING_FACETS = {
	"hierarchicalCategories.lvl0": { kind: "list", param: "department" },
	"hierarchicalCategories.lvl1": { kind: "list", param: "category" },
	"specification.materials": { kind: "list", param: "material" },
	"collections.title": { kind: "list", param: "collection" },
	priceBucket: { kind: "list", param: "price" },
	ratingBucket: { kind: "list", param: "rating" },
	tagCodes: { kind: "list", param: "tags" },
	"tagCodesByKind.highlight": {
		kind: "list",
		param: "highlight",
		matchesAll: true
	},
	"tagCodesByKind.care": { kind: "list", param: "care" },
	"tagCodesByKind.guarantee": { kind: "list", param: "guarantee" },
	"tagCodesByKind.dietary": { kind: "list", param: "dietary" },
	onSale: { kind: "toggle", param: "sale" },
	onlineStatus: { kind: "toggle", param: "inStock" },
	salePrice: { kind: "range", param: "priceRange" }
} as const satisfies Record<string, ListingFacet>;

export type ListingFacetAttribute = keyof typeof LISTING_FACETS;

export const LISTING_FACET_ATTRIBUTES = Object.keys(
	LISTING_FACETS
) as ListingFacetAttribute[];

export type ListingFacetFilters = (string | string[])[];

const IN_STOCK_STATUSES = LISTING_STATUSES.filter(
	(status) => status !== "out-of-stock"
);

export const LISTING_TOGGLE_VALUES: Partial<
	Record<ListingFacetAttribute, readonly string[]>
> = {
	onSale: ["true"],
	onlineStatus: IN_STOCK_STATUSES
};

// In the design's order. A sort without a boost needs its replica in search-sync products-index-settings.ts.
export const LISTING_SORTS = [
	"price_asc",
	"price_desc",
	"rating_desc",
	"bestseller",
	"newest"
] as const;

export type ListingSort = (typeof LISTING_SORTS)[number];

const SORT_BOOSTS: Partial<Record<ListingSort, string[]>> = {
	bestseller: ["tagCodes:highlight-bestseller"]
};

export interface ListingSortQuery {
	replica?: string;
	optionalFilters?: string[];
}

export function isListingFacetAttribute(
	value: string
): value is ListingFacetAttribute {
	return Object.prototype.hasOwnProperty.call(LISTING_FACETS, value);
}

export function listingFilterKind(
	attribute: ListingFacetAttribute
): ListingFilterKind {
	return LISTING_FACETS[attribute].kind;
}

export function listingFilterParam(attribute: ListingFacetAttribute): string {
	return LISTING_FACETS[attribute].param;
}

export function listingFilterOperator(
	attribute: ListingFacetAttribute
): "and" | "or" {
	const facet: ListingFacet = LISTING_FACETS[attribute];
	return facet.matchesAll ? "and" : "or";
}

export function isListingSort(value: unknown): value is ListingSort {
	return (
		typeof value === "string" &&
		(LISTING_SORTS as readonly string[]).includes(value)
	);
}

export function toListingSortQuery(value: unknown): ListingSortQuery {
	if (!isListingSort(value)) return {};
	const boost = SORT_BOOSTS[value];
	return boost ? { optionalFilters: boost } : { replica: value };
}

function isListingFacetFilter(value: unknown): value is string {
	if (typeof value !== "string") return false;
	const attribute = value.replace(/^-/, "").split(":")[0];
	return isListingFacetAttribute(attribute);
}

const NUMERIC_FILTER = /^([A-Za-z.]+)\s*(<=|>=|<|>|=)\s*(-?\d+(\.\d+)?)$/;

function isListingNumericFilter(value: unknown): value is string {
	if (typeof value !== "string") return false;
	const attribute = NUMERIC_FILTER.exec(value)?.[1];
	return (
		attribute !== undefined &&
		isListingFacetAttribute(attribute) &&
		listingFilterKind(attribute) === "range"
	);
}

export function toListingFacets(value: unknown): ListingFacetAttribute[] {
	const facets = Array.isArray(value) ? value : [value];
	return facets.filter(
		(facet): facet is ListingFacetAttribute =>
			typeof facet === "string" && isListingFacetAttribute(facet)
	);
}

function keepEntries(
	value: unknown,
	keep: (entry: unknown) => entry is string
): ListingFacetFilters {
	if (!Array.isArray(value)) return [];

	return value.flatMap((entry): ListingFacetFilters => {
		if (Array.isArray(entry)) {
			const anyOf = entry.filter(keep);
			return anyOf.length > 0 ? [anyOf] : [];
		}
		return keep(entry) ? [entry] : [];
	});
}

export function toListingFacetFilters(value: unknown): ListingFacetFilters {
	return keepEntries(value, isListingFacetFilter);
}

export function toListingNumericFilters(value: unknown): ListingFacetFilters {
	return keepEntries(value, isListingNumericFilter);
}
